// useRooms — read-only accessor for joined Matrix rooms.
//
// A single `subscribeRooms` subscription lives in `MatrixContext` and
// feeds every surface. This hook is the thin read boundary the sidebar
// calls. DMs are listed regardless of the active space; channels are
// scoped to it.

import { useMemo } from "react";

import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import type { RoomSummary } from "~/lib/matrix";
import { repairSpaceSelection, visibleRoomsForSpace } from "~/lib/spaces";

/**
 * Return the joined, non-archived rooms the sidebar lists: channels of the
 * active space (repaired the same way `RoomList` repairs it), rooms that no
 * space claims while an org space is active, and every DM.
 */
export function useRooms(): RoomSummary[] {
  const { activeOrgSpaceId, activeSpaceId } = useMatrix();
  const rooms = useMatrixRooms();

  return useMemo(() => {
    const repaired = repairSpaceSelection(
      rooms,
      activeOrgSpaceId,
      activeSpaceId && activeSpaceId !== activeOrgSpaceId ? activeSpaceId : null,
    );
    return visibleRoomsForSpace(rooms, repaired.activeSpaceId).filter(
      (room) => room.archived === null,
    );
  }, [rooms, activeOrgSpaceId, activeSpaceId]);
}
